'use client';

import { useMemo } from 'react';
import { useQuery } from '@zg-rentals/react-query5';
import type { ExtendedListing } from '@/src/lib/api/endpoints/listing';
import { searchListings } from '@/src/lib/api/endpoints/search';
import { capV3Photos, normalizeV3Listings } from '@/src/lib/utils/v3Listings';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { getRecommendationListingsRequest, type RecommendationListingsKind } from './recommendationListings.utils';

export const recommendationListingsKeys = {
  all: ['halo', 'recommendationListings'] as const,
  byListing: (
    kind: RecommendationListingsKind,
    maloneLotIdEncoded: string | null | undefined,
    aliasEncoded: string | null | undefined,
  ) => ['halo', 'recommendationListings', kind, maloneLotIdEncoded ?? '', aliasEncoded ?? ''] as const,
};

export function useRecommendationListings(
  listing: PropertyDetailListing | null | undefined,
  kind: RecommendationListingsKind,
  { enabled }: { enabled: boolean },
) {
  const request = useMemo(() => getRecommendationListingsRequest(listing, kind), [kind, listing]);

  return useQuery<Array<ExtendedListing>>({
    queryKey: recommendationListingsKeys.byListing(kind, listing?.maloneLotIdEncoded, listing?.aliasEncoded),
    queryFn: async ({ signal }) => {
      if (!request) return [];
      const response = await searchListings(request, signal);
      return normalizeV3Listings(capV3Photos(response.listings));
    },
    enabled: request != null && enabled,
    staleTime: 5 * 60 * 1000,
    meta: {
      tags: ['search-listings'],
      context: {
        api: 'hotpads-api',
        details: 'HDP recommendation listings',
      },
    },
  });
}
